import {
  CalAppleImage,
  CalAppleImageDark,
  CalGoogleImage,
  CalGoogleImageDark,
  CalOutlookImage,
  CalOutlookImageDark,
  GroupDefaultImage,
  GroupDefaultImageDark
} from 'assets/icons'
import {
  differenceInDays,
  differenceInSeconds,
  format,
  isToday,
  isYesterday
} from 'date-fns'
import { t } from 'i18next'
import { GroupPrivacy } from 'model/models/Group'
import { Account } from 'services/db/Queries/AccountQueries'
import { allGroups, localGroupById } from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { getResolvedThemeModeFromDom } from 'theme'
import {
  AccountDoc,
  EventDoc,
  GroupDoc,
  GroupParticipant,
  GroupParticipantStatus,
  GroupParticipantType,
  LastMessage,
  ParticipantDataDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  getAllowedAdminsForTier,
  getMinimumTierForAdminCount
} from 'utils/api/tiers'
import { is24meApp } from 'utils/appType'
import {
  defaultGroupId,
  groupIdToProvider,
  isConvertedGroup,
  PersonalGroupSyncStatus,
  ProviderType
} from 'utils/groups'
import { difference, isEmpty, isEqual, isTrulyEmpty } from 'utils/object'

import channelDefaultPhoto from '../../assets/icons/channel_calendar.svg'
import i18n from '../../i18n'
import { highestActiveTier, Tier } from './accounts'
import { TierFeatures } from './group_state'
import { isGroupArchived, isGroupHidden } from './userSettings'

export function groupHasMembersWithColor(group: GroupDoc) {
  if (group.Participants) {
    const participants = Object.values(group.Participants)

    if (participants.length > 0) {
      return participants.some(
        (participant) =>
          participant.MemberColor &&
          participant.MemberColor.length > 0 &&
          participant.MemberColor != 'null'
      )
    }
  }

  return false
}

export function amountOfAdminsInGroup(group: GroupDoc) {
  let admins = 0

  if (group.Participants) {
    const participants = Object.values(group.Participants)

    if (participants.length > 0) {
      participants.forEach((participant) => {
        if (participant.Type === GroupParticipantType.ADMIN) admins += 1
      })
    }
  }

  return admins
}

export function isAllPermissionsEnabled(group: GroupDoc) {
  return (
    group.GroupSettings.IsAllParticipantsCanAddItems === '1' &&
    group.GroupSettings.IsAllParticipantsCanAddParticipants === '1' &&
    group.GroupSettings.IsAllParticipantsCanEditGroupMetadata === '1'
  )
}

export function isEventDetailsVisible(group: GroupDoc | undefined) {
  if (!group) return false

  return (
    group.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly === undefined ||
    group.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly === '1'
  )
}

export function isUserLastActiveInGroup(
  group: GroupDoc | undefined,
  userID: string
) {
  if (!group) return false

  let activeParticipants = 0
  for (const participantId in group.Participants) {
    const participant = group.Participants[participantId]
    if (participant.Status === GroupParticipantStatus.ACTIVE) {
      activeParticipants += 1
    }
  }

  return (
    activeParticipants === 1 &&
    group.Participants[userID]?.Status === GroupParticipantStatus.ACTIVE
  )
}

export function isUserActiveInGroup(
  group: GroupDoc | undefined,
  userID: string
) {
  if (!group || (isConvertedGroup(group) && isTrulyEmpty(group._rev)))
    return true

  const participant = group?.Participants?.[userID]

  return participant?.Status === GroupParticipantStatus.ACTIVE
}

export function isGroupDocRecentlyUpdated(group: GroupDoc): boolean {
  const creationDate = new Date(Number(group.CreationDate) * 1000)
  const updateDate = new Date((Number(group.LastUpdate) ?? 0) * 1000)
  return (
    differenceInDays(updateDate, creationDate) < 1 &&
    differenceInSeconds(updateDate, creationDate) < 5
  )
}

function filterActiveMembers(participant: GroupParticipant) {
  return participant?.Status === GroupParticipantStatus.ACTIVE
}

function groupActiveMembersCount(group: GroupDoc): number {
  return (
    Object.values(group.Participants || {}).filter(filterActiveMembers).length +
    Object.values(group.PendingParticipants || {}).filter(filterActiveMembers)
      .length
  )
}

export function isGroupBlockedToAddEvents(
  account: AccountDoc,
  group: GroupDoc,
  userSettings: UserSettingsDoc | undefined,
  userId: string,
  withHiddenCheck = true
) {
  if (!group) return true

  return (
    isGroupOutOfTier(highestActiveTier(account), group, account) ||
    group?.SyncProblem === '1' ||
    !isUserCanOperateEvents(group, userId) ||
    !isUserActiveInGroup(group, userId) ||
    (withHiddenCheck && isGroupHidden(userSettings, group._id ?? ''))
  )
}

export function isUserCanOperateEvents(group?: GroupDoc, userId?: string) {
  if (is24meApp()) return true

  if (!group || !userId) return false

  if (
    isConvertedGroup(group) &&
    group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
  )
    return true

  const isAdmin =
    group.Participants?.[userId]?.Type === GroupParticipantType.ADMIN
  const canAddAll = group.GroupSettings?.IsAllParticipantsCanAddItems === '1'

  return isAdmin || canAddAll || group.OwnerID === userId
}

export function isUserAdminInGroup(
  group: GroupDoc | undefined,
  userID: string
) {
  return (
    group?.Participants?.[userID]?.Type === GroupParticipantType.ADMIN ||
    group?.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
  )
}

export function isUserOwnerOfGroup(group: GroupDoc, userID: string) {
  return group.OwnerID === userID
}

function addGroupMessage(group: GroupDoc, message?: LastMessage): GroupDoc {
  return {
    ...group,
    _sortOrder:
      group._sortOrder === 0 ? Number(group.LastUpdate) : group._sortOrder,
    LastUpdateMessage: message,
    Name: group.DeviceChangeID?.toLowerCase().includes('prepopulated_group')
      ? nameForPrepopulatedGroup(group)
      : group.Name,
    UserID: LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  }
}

function addGroupSortOrder(group: GroupDoc, sortOrder: number): GroupDoc {
  return {
    ...group,
    _sortOrder: sortOrder
  }
}

export function updateGroupDocMessage(
  userID: string,
  newGroup: GroupDoc,
  prevGroup: GroupDoc,
  participants: Model<ParticipantDataDoc>[]
): GroupDoc {
  // ignore such updates for converted calendars

  // if (isConvertedGroup(prevGroup)) return prevGroup

  if (
    prevGroup &&
    !isUserActiveInGroup(
      prevGroup,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) &&
    !isUserActiveInGroup(
      newGroup,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )
  ) {
    /**
     * If user are inactive in a group and we receied update about it
     * we need to skip it
     */

    return prevGroup
  }

  const newGroupLastUpdate = Number(newGroup.LastUpdate)

  const isCurrentUserChangedEvent = newGroup.UserID === userID
  const isActiveInGroup = isUserActiveInGroup(newGroup, userID)
  const userMadeAction = participants.find(
    (participant) => participant.AccountID === newGroup.UserID
  )

  let userTitleMadeChange =
    userMadeAction?.FullName ?? userMadeAction?.PhoneNumber ?? ''

  if (isCurrentUserChangedEvent) userTitleMadeChange = t('you')

  let groupParameterWhichChanged = ''

  if (isGroupDocRecentlyUpdated(newGroup)) {
    if (newGroup.DeviceChangeID?.toLowerCase().includes('prepopulated_group')) {
      return addGroupMessage(
        { ...newGroup, Name: nameForPrepopulatedGroup(newGroup) },
        { type: `${i18n.t('prepopulatedCreated')}` }
      )
    } else if (newGroup.OwnerID === userID) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, { type: `${i18n.t('youCreatedCalendar')}` }),
        newGroupLastUpdate
      )
    } else {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('calendarCreated')}`,
          who: userTitleMadeChange
        }),
        newGroupLastUpdate
      )
    }
  }

  if (!isActiveInGroup) {
    const user = newGroup.Participants?.[userID]
    const removedBy = user?.RemovedBy

    if (removedBy && removedBy === userID) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('youLeft')}`
        }),
        newGroupLastUpdate
      )
    } else {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('removedYouGeneral')}`,
          who: userTitleMadeChange
        }),
        newGroupLastUpdate
      )
    }
  }

  if (
    !prevGroup ||
    (prevGroup && !isUserActiveInGroup(prevGroup, userID) && isActiveInGroup)
  ) {
    if (newGroup.OwnerID === userID) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('youCreatedCalendar')}`
        }),
        newGroupLastUpdate
      )
    } else {
      if (newGroup.Participants[userID].InvitedBy === 'sharelink') {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: `${i18n.t('youJoined')}`
          }),
          newGroupLastUpdate
        )
      }

      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('youAdded')}`
        }),
        newGroupLastUpdate
      )
    }
  }

  if (prevGroup) {
    // const updatedGroupHasPhoto = newGroup.Photo !== null && newGroup.Photo;

    if (newGroup.Photo !== prevGroup.Photo && newGroup.Photo) {
      groupParameterWhichChanged = `${i18n.t('photo')}`
    }

    if (newGroup.GroupColor !== prevGroup.GroupColor) {
      groupParameterWhichChanged = `${i18n.t('color')}`
    }

    if (newGroup.Name !== prevGroup.Name) {
      groupParameterWhichChanged = `${i18n.t('name')}`
    }

    if (
      newGroup.GroupSettings.IsAllParticipantsCanAddItems !==
        prevGroup.GroupSettings.IsAllParticipantsCanAddItems ||
      newGroup.GroupSettings.IsAllParticipantsCanAddParticipants !==
        prevGroup.GroupSettings.IsAllParticipantsCanAddParticipants ||
      newGroup.GroupSettings.IsAllParticipantsCanEditGroupMetadata !==
        prevGroup.GroupSettings.IsAllParticipantsCanEditGroupMetadata ||
      newGroup.GroupSettings.IsAllParticipantsCanEditOnlyTheirEvents !==
        prevGroup.GroupSettings.IsAllParticipantsCanEditOnlyTheirEvents ||
      newGroup.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly !==
        prevGroup.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly
    ) {
      groupParameterWhichChanged = `${i18n.t('permissions')}`
    }

    if (groupParameterWhichChanged) {
      // In case GroupName is changed, we need to move group up!
      // If changed Photo, Color etc... no need to move group up - use prev sortOrder.
      const sortOrder =
        groupParameterWhichChanged.length > 0
          ? newGroup.LastUpdate
          : prevGroup._sortOrder

      if (isCurrentUserChangedEvent) {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: `${i18n.t('youChangedInCal', { groupParameterWhichChanged })}`
          }),
          Number(sortOrder)
        )
      } else {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: `${i18n.t('changedInCalGeneral', {
              groupParameterWhichChanged
            })}`
          }),
          Number(sortOrder)
        )
      }
    }

    const previousGroupMembersCount = groupActiveMembersCount(prevGroup)
    const updatedGroupMembersCount = groupActiveMembersCount(newGroup)

    if (
      isUserOwnerOfGroup(newGroup, userID) &&
      !isUserOwnerOfGroup(prevGroup, userID)
    ) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: !isTrulyEmpty(userTitleMadeChange)
            ? `${i18n.t('madeYouOwner', { userTitleMadeChange })}`
            : `${i18n.t('youNowOwner')}`
        }),
        newGroupLastUpdate
      )
    }

    if (
      isUserAdminInGroup(newGroup, userID) &&
      !isUserAdminInGroup(prevGroup, userID)
    ) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: !isTrulyEmpty(userTitleMadeChange)
            ? `${i18n.t('madeYouAdmin', { userTitleMadeChange })}`
            : `${i18n.t('youNowAdmin')}`
        }),
        newGroupLastUpdate
      )
    }

    if (
      !isUserOwnerOfGroup(newGroup, userID) &&
      isUserOwnerOfGroup(prevGroup, userID)
    ) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('youNoLongerOwner')}`
        }),
        newGroupLastUpdate
      )
    }

    if (
      !isUserAdminInGroup(newGroup, userID) &&
      isUserAdminInGroup(prevGroup, userID)
    ) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, {
          type: `${i18n.t('youNoLongerAdmin')}`
        }),
        newGroupLastUpdate
      )
    }

    if (updatedGroupMembersCount > previousGroupMembersCount) {
      if (isCurrentUserChangedEvent) {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: `${i18n.t('youAddedParticipants')}`
          }),
          prevGroup._sortOrder
        )
      } else {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: userTitleMadeChange
              ? `${i18n.t('userAddedMembers', { userTitleMadeChange })}`
              : `${i18n.t('membersAdded')}`
          }),
          prevGroup._sortOrder
        )
      }
    }

    const participantsChange: Record<
      string,
      Partial<GroupParticipant>
    > = difference(prevGroup.Participants, newGroup.Participants)

    if (!isEmpty(participantsChange)) {
      const participantChangeIds = Object.keys(participantsChange)

      if (participantChangeIds.includes(newGroup.UserID ?? '')) {
        if (
          participantsChange[newGroup.UserID ?? ''].Status ===
          GroupParticipantStatus.INACTIVE
        ) {
          return addGroupSortOrder(
            addGroupMessage(newGroup, {
              type: `${i18n.t('memberLeft')}`
            }),
            prevGroup._sortOrder
          )
        }
      }

      const removedMembers = Object.values(participantsChange).filter(
        (participant) => participant.Status === GroupParticipantStatus.INACTIVE
      )

      if (removedMembers.length) {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: isCurrentUserChangedEvent
              ? `${i18n.t('youRemovedMembers')}`
              : userTitleMadeChange
                ? `${i18n.t('userRemovedMembers', { userTitleMadeChange })}`
                : `${i18n.t('membersRemovedGeneral')}`
          }),
          prevGroup._sortOrder
        )
      }
    }

    if (!isEqual(newGroup.GroupSettings, prevGroup.GroupSettings)) {
      if (isCurrentUserChangedEvent) {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: `${i18n.t('youChangedPermissions')}`
          }),
          prevGroup._sortOrder
        )
      } else {
        return addGroupSortOrder(
          addGroupMessage(newGroup, {
            type: userTitleMadeChange
              ? `${i18n.t('userChangedPermissions', { userTitleMadeChange })}`
              : `${i18n.t('permissionsChangeGeneral')}`
          }),
          prevGroup._sortOrder
        )
      }
    }

    if (!isEqual(newGroup.BusinessInfo?.Text, prevGroup.BusinessInfo?.Text)) {
      return addGroupSortOrder(
        addGroupMessage(newGroup, prevGroup?.LastUpdateMessage),
        prevGroup._sortOrder
      )
    }

    return addGroupSortOrder(
      addGroupMessage(newGroup, prevGroup?.LastUpdateMessage),
      prevGroup._sortOrder
    )
  }

  return addGroupMessage(newGroup, (prevGroup as GroupDoc)?.LastUpdateMessage)
}

export function formatLastUpdate(lastUpdate: number): string {
  if (!lastUpdate) return ''

  const date = new Date(lastUpdate * 1000)

  if (isToday(date)) {
    return format(date, 'h:mm aa')
  }

  if (isYesterday(date)) {
    return i18n.t('yesterday')
  }

  return format(date, 'dd/MM/yyyy')
}

export function getGroupMessage(
  group?: Model<GroupDoc> | GroupDoc,
  event?: Model<EventDoc>
): LastMessage {
  const eventBased =
    event?.LastUpdateMessage != undefined
      ? event.LastUpdateMessage
      : ({ type: event?._message } as LastMessage)
  const groupBased =
    group?.LastUpdateMessage != undefined
      ? group.LastUpdateMessage
      : ({ type: group?._message } as LastMessage)

  const eventLastUpdate = Number(event?.LastUpdate) || 0
  const groupLastUpdate = Number(group?._sortOrder) || 0

  return eventLastUpdate >= groupLastUpdate && eventLastUpdate !== 0
    ? eventBased
    : groupBased
}

export function groupsToImages(groups?: Model<GroupDoc>[]): string[] {
  if (!groups) return []
  return groups.map((group) => {
    return group.Photo ? group.Photo : defaultGroupImage(group)
  })
}

export function defaultConvertedCalendarPhoto(groupId: string | undefined) {
  const isDarkMode = getResolvedThemeModeFromDom() === 'dark'
  const regularGroupImage = isDarkMode
    ? GroupDefaultImageDark
    : GroupDefaultImage

  if (!groupId) return regularGroupImage

  const provider = groupIdToProvider(groupId)

  if (provider === ProviderType.GOOGLE) {
    return isDarkMode ? CalGoogleImageDark : CalGoogleImage
  } else if (provider === ProviderType.OUTLOOK) {
    return isDarkMode ? CalOutlookImageDark : CalOutlookImage
  } else if (provider === ProviderType.APPLE) {
    return isDarkMode ? CalAppleImageDark : CalAppleImage
  }

  return regularGroupImage
}

export function defaultGroupImage(group?: GroupDoc): any {
  const isDarkMode = getResolvedThemeModeFromDom() === 'dark'
  const regularGroupImage = isDarkMode
    ? GroupDefaultImageDark
    : GroupDefaultImage

  if (!group) return regularGroupImage

  if (isConvertedGroup(group)) {
    if (!isTrulyEmpty(group.CalendarEmail)) {
      return defaultConvertedCalendarPhoto(group.CalendarEmail)
    }

    return defaultConvertedCalendarPhoto(group._id)
  }

  return group.PrivacyMode === GroupPrivacy.CHANNEL
    ? channelDefaultPhoto
    : regularGroupImage
}

export function getActiveParticipants(
  participants: Record<string, GroupParticipant>
) {
  if (!participants) return []
  const activeParticipants = Object.keys(participants)
    .filter(function (key) {
      const entry = participants[key]
      return entry.Status === GroupParticipantStatus.ACTIVE
    })
    .reduce(
      (res: Record<string, GroupParticipant>, key) => (
        (res[key] = participants[key]), res
      ),
      {}
    )

  return activeParticipants
}

export function isAddingMemberAllowedForGroup(group: GroupDoc) {
  if (!group) return false

  if (
    !isUserActiveInGroup(group, LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
  ) {
    return false
  }

  if (
    isUserOwnerOfGroup(group, LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
  )
    return true

  if (
    isUserAdminInGroup(group, LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
  )
    return true

  return group.GroupSettings.IsAllParticipantsCanAddParticipants === '1'
}

/**
 * Whether the given group is allowed to participate in the linked calendars
 * feature (as parent or child). Defaults to '1' (allowed) for non-personal
 * calendars when the setting is missing; personal (converted) calendars are
 * disallowed by default.
 */
export function isCalendarLinkingAllowed(group: GroupDoc | undefined): boolean {
  if (!group) return false

  const value = group.GroupSettings?.IsCalendarLinkingAllowed

  if (value === '1') return true
  if (value === '0') return false

  return !isConvertedGroup(group)
}

export function isParticipantsEditingTheirEventsOnly(
  group: GroupDoc | undefined
): boolean {
  if (!group) return false

  return group.GroupSettings.IsAllParticipantsCanEditOnlyTheirEvents === '1'
}

export function isEventsActionsAllowed(
  group: GroupDoc | undefined,
  userId: string,
  event: EventDoc | undefined
) {
  if (!group || (isConvertedGroup(group) && event?.OwnerID === userId))
    return true

  return (
    group.OwnerID === userId ||
    (group.GroupSettings.IsAllParticipantsCanAddItems === '1' &&
      isUserActiveInGroup(group, userId)) ||
    (event?.OwnerID === userId && isParticipantsEditingTheirEventsOnly(group))
  )
}

export function provideNeededTierForGroup(
  currentTier: Tier,
  group?: GroupDoc
): Tier {
  if (isGroupOutOfTier(currentTier, group)) {
    const features = outOfTiersFeaturesForGroup(currentTier, group)

    let largest = Tier.FREE

    features.forEach((feature) => {
      const newLargest = featureToMinimumTierNeeded(
        feature,
        feature === TierFeatures.ADMINS && group
          ? amountOfAdminsInGroup(group)
          : undefined
      )

      if (newLargest > largest) largest = newLargest
    })

    return largest
  }

  return Tier.FREE
}

export function isGroupOutOfTier(
  currentTier: Tier,
  group?: GroupDoc,
  accountDoc?: AccountDoc
) {
  if (!group) return false
  if (group.OwnerID != LocalStorage.get(LocalStorageKey.USER_ID)) return false

  return outOfTiersFeaturesForGroup(currentTier, group, accountDoc).length > 0
}

export function isGroupChannel(group?: GroupDoc) {
  if (!group) return false

  return group.PrivacyMode === GroupPrivacy.CHANNEL
}

export function outOfTiersFeaturesForGroup(
  tier: Tier,
  group?: GroupDoc,
  account?: AccountDoc
): TierFeatures[] {
  let outOfTierFeatures: TierFeatures[] = []
  if (
    !isFeatureAllowedToUser(
      TierFeatures.SEE_EVENTS_DETAILS,
      tier,
      group,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.SEE_EVENTS_DETAILS)
  if (!isFeatureAllowedToUser(TierFeatures.ADMINS, tier, group, account))
    outOfTierFeatures.push(TierFeatures.ADMINS)
  if (!isFeatureAllowedToUser(TierFeatures.BUSINESSINFO, tier, group, account))
    outOfTierFeatures.push(TierFeatures.BUSINESSINFO)
  if (!isFeatureAllowedToUser(TierFeatures.CHANNEL, tier, group, account))
    outOfTierFeatures.push(TierFeatures.CHANNEL)
  if (!isFeatureAllowedToUser(TierFeatures.LANDLINE, tier, group, account))
    outOfTierFeatures.push(TierFeatures.LANDLINE)
  if (!isFeatureAllowedToUser(TierFeatures.MEMBERCOLOR, tier, group, account))
    outOfTierFeatures.push(TierFeatures.MEMBERCOLOR)
  if (
    !isFeatureAllowedToUser(
      TierFeatures.PERMISSIONS_CALENDAR_NAME,
      tier,
      group,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.PERMISSIONS_CALENDAR_NAME)
  if (
    !isFeatureAllowedToUser(
      TierFeatures.PERMISSIONS_EVENTS,
      tier,
      group,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.PERMISSIONS_EVENTS)
  if (
    !isFeatureAllowedToUser(
      TierFeatures.PERMISSIONS_MEMBERS,
      tier,
      group,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.PERMISSIONS_MEMBERS)
  if (!isFeatureAllowedToUser(TierFeatures.VERIFIED, tier, group, account))
    outOfTierFeatures.push(TierFeatures.VERIFIED)
  if (
    !isFeatureAllowedToUser(TierFeatures.LINK_CALENDARS, tier, group, account)
  )
    outOfTierFeatures.push(TierFeatures.LINK_CALENDARS)
  if (
    !isFeatureAllowedToUser(
      TierFeatures.EDIT_THEIR_EVENTS_ONLY,
      tier,
      group,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.EDIT_THEIR_EVENTS_ONLY)

  if (
    !isFeatureAllowedToUser(
      TierFeatures.SHARE_PERSONAL_CALENDAR,
      tier,
      group,
      account
    )
  ) {
    outOfTierFeatures = [] // for personal calendars we show only this out of tier
    outOfTierFeatures.push(TierFeatures.SHARE_PERSONAL_CALENDAR)
  }

  return outOfTierFeatures
}

export function isFeatureAllowedToUser(
  feature: TierFeatures,
  currentTier: Tier,
  group?: GroupDoc,
  accountDoc?: AccountDoc
) {
  if (group && group.OwnerID != LocalStorage.get(LocalStorageKey.USER_ID))
    return true // for all old groups or for non-owners, we mark all features as allowed

  switch (feature) {
    case TierFeatures.SHARE_PERSONAL_CALENDAR:
      if (is24meApp()) return true

      if (
        isConvertedGroup(group) &&
        currentTier <
          featureToMinimumTierNeeded(TierFeatures.SHARE_PERSONAL_CALENDAR) &&
        ((!isTrulyEmpty(group?.PrivateLinkUrl) &&
          !isTrulyEmpty(group?.CalendarEmail)) ||
          (!isTrulyEmpty(group?.PersonalGroupSyncStatus) &&
            group?.PersonalGroupSyncStatus !== PersonalGroupSyncStatus.PRIVATE))
      ) {
        return false
      }

      break
    case TierFeatures.RSVP:
      if (currentTier < featureToMinimumTierNeeded(TierFeatures.RSVP))
        return false
      break
    case TierFeatures.PRIORITY_SUPPORT:
      if (
        currentTier < featureToMinimumTierNeeded(TierFeatures.PRIORITY_SUPPORT)
      )
        return false
      break

    case TierFeatures.LANDLINE:
      if (group) {
        if (group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)) {
          if (
            accountDoc &&
            currentTier < featureToMinimumTierNeeded(TierFeatures.LANDLINE) &&
            accountDoc.PhoneNumberType === '2'
          )
            return false
        }
      } else {
        if (
          currentTier < featureToMinimumTierNeeded(TierFeatures.LANDLINE) &&
          accountDoc &&
          accountDoc.PhoneNumberType === '2'
        )
          return false
      }
      break

    case TierFeatures.ADMINS:
      if (Number(group?.Version ?? '1') < 2) return true
      if (group) {
        if (amountOfAdminsInGroup(group) > allowedAdminsForTier(currentTier)) {
          return false
        } else {
          return true
        }
      }
      break

    case TierFeatures.BUSINESSINFO:
      if (group) {
        if (
          group.BusinessInfo?.Active === '1' &&
          currentTier < featureToMinimumTierNeeded(TierFeatures.BUSINESSINFO)
        )
          return false
        else return true
      }

      if (currentTier < featureToMinimumTierNeeded(TierFeatures.BUSINESSINFO))
        return false
      break
    case TierFeatures.PERMISSIONS:
      if (
        currentTier < featureToMinimumTierNeeded(TierFeatures.PERMISSIONS) &&
        group &&
        !isAllPermissionsEnabled(group)
      )
        return false
      break
    case TierFeatures.PERMISSIONS_CALENDAR_NAME:
      if (
        group?.GroupSettings &&
        currentTier <
          featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_CALENDAR_NAME) &&
        group &&
        group.GroupSettings.IsAllParticipantsCanEditGroupMetadata != '1'
      ) {
        if (isConvertedGroup(group) && isTrulyEmpty(group.PrivateLinkUrl)) {
          return true
        }
        return false
      }
      break
    case TierFeatures.PERMISSIONS_EVENTS:
      if (
        group?.GroupSettings &&
        currentTier <
          featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_EVENTS) &&
        group &&
        group.GroupSettings.IsAllParticipantsCanAddItems != '1'
      ) {
        if (isConvertedGroup(group) && isTrulyEmpty(group.PrivateLinkUrl)) {
          return true
        }
        return false
      }
      break
    case TierFeatures.PERMISSIONS_MEMBERS:
      if (
        group?.GroupSettings &&
        currentTier <
          featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_MEMBERS) &&
        group &&
        group.GroupSettings.IsAllParticipantsCanAddParticipants != '1'
      ) {
        if (isConvertedGroup(group) && isTrulyEmpty(group.PrivateLinkUrl)) {
          return true
        }

        return false
      }
      break
    case TierFeatures.VERIFIED:
      if (group) {
        if (
          currentTier < featureToMinimumTierNeeded(TierFeatures.VERIFIED) &&
          group.VerifiedGroupStatus === '2'
        ) {
          return false
        } else {
          return true
        }
      }
      if (currentTier < featureToMinimumTierNeeded(TierFeatures.VERIFIED))
        return false
      break

    case TierFeatures.CHANNEL:
      if (group) {
        if (
          group.PrivacyMode === GroupPrivacy.CHANNEL &&
          currentTier < featureToMinimumTierNeeded(TierFeatures.CHANNEL)
        ) {
          return false
        } else {
          return true
        }
      }
      if (currentTier < Tier.BUSINESS_PLUS) return false
      break
    case TierFeatures.MEMBERCOLOR:
      if (group) {
        if (
          currentTier < featureToMinimumTierNeeded(TierFeatures.MEMBERCOLOR) &&
          groupHasMembersWithColor(group)
        ) {
          return false
        } else {
          return true
        }
      }
      if (currentTier < featureToMinimumTierNeeded(TierFeatures.MEMBERCOLOR))
        return false
      break
    case TierFeatures.SEE_EVENTS_DETAILS:
      if (
        currentTier <
          featureToMinimumTierNeeded(TierFeatures.SEE_EVENTS_DETAILS) &&
        group?.GroupSettings?.IsAllParticipantsCanSeeFreeBusyOnly === '1'
      )
        return false
      break
    case TierFeatures.SYNC_WITH_CALENDARS:
      if (
        currentTier <
        featureToMinimumTierNeeded(TierFeatures.SYNC_WITH_CALENDARS)
      )
        return false
      break
    case TierFeatures.LINK_CALENDARS:
      if (
        Object.keys(group?.ParentGroups ?? {}).length > 0 &&
        currentTier < featureToMinimumTierNeeded(TierFeatures.LINK_CALENDARS)
      )
        return false
      break
    case TierFeatures.EDIT_THEIR_EVENTS_ONLY:
      if (
        currentTier <
          featureToMinimumTierNeeded(TierFeatures.EDIT_THEIR_EVENTS_ONLY) &&
        group &&
        group?.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents === '1'
      )
        return false
      else return true

    default:
      return true
  }

  return true
}

function allowedAdminsForTier(tier: Tier) {
  return getAllowedAdminsForTier(tier)
}

/**
 *
 * @param feature to check
 * @param amount optional param for features such as admins
 * @returns lowest needed tier to use feature
 */
export function featureToMinimumTierNeeded(
  feature: TierFeatures,
  amount?: number
) {
  switch (feature) {
    case TierFeatures.PRIORITY_SUPPORT:
    case TierFeatures.MEMBERCOLOR:
    case TierFeatures.PERMISSIONS:
    case TierFeatures.PERMISSIONS_CALENDAR_NAME:
    case TierFeatures.PERMISSIONS_EVENTS:
    case TierFeatures.PERMISSIONS_MEMBERS:
    case TierFeatures.RSVP:
    case TierFeatures.SHARE_PERSONAL_CALENDAR:
      return Tier.PRO

    case TierFeatures.BUSINESSINFO:
    case TierFeatures.LANDLINE:
    case TierFeatures.ASSIGN_EVENT_OWNERSHIP:
    case TierFeatures.EDIT_THEIR_EVENTS_ONLY:
    case TierFeatures.SEE_EVENTS_DETAILS:
      return Tier.BUSINESS

    case TierFeatures.CHANNEL:
    case TierFeatures.VERIFIED:
    case TierFeatures.SYNC_WITH_CALENDARS:
    case TierFeatures.EMBED_CALENDARS:
    case TierFeatures.LINK_CALENDARS:
      return Tier.BUSINESS_PLUS

    case TierFeatures.ADMINS:
      return getMinimumTierForAdminCount(amount)

    default:
      // Handle default case if necessary
      return Tier.FREE
  }
}
function nameForPrepopulatedGroup(newGroup: GroupDoc): string {
  switch (newGroup.DeviceChangeID) {
    case 'Prepopulated_Group_work':
      return i18n.t('preGroupWork')

    case 'Prepopulated_Group_family':
      return i18n.t('preGroupFamily')

    case 'Prepopulated_Group_vacation':
      return i18n.t('preGroupVacation')
  }

  return ''
}

export function isGroupNotAvailableForEventChanges(
  group: GroupDoc,
  account: AccountDoc
): boolean {
  const activeTier = highestActiveTier(account)

  if (isConvertedGroup(group) && group.SyncProblem === '1') return true

  return (
    isGroupOutOfTier(activeTier, group, account) ||
    LocalStorage.get(LocalStorageKey.USER_ID)?.length === 0 ||
    !isUserActiveInGroup(
      group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) ||
    !isUserCanOperateEvents(
      group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )
  )
}

/** function returns first group id (preferebly default one)) where user can add events */
export async function isUserHaveGroupToAddEvents(): Promise<
  string | undefined
> {
  const us = await UserSettings()
  const account = await Account()
  const defaultGroup = await localGroupById(defaultGroupId(us) ?? '')

  if (
    defaultGroup &&
    defaultGroup.SyncProblem !== '1' &&
    !isGroupArchived(us, defaultGroup._id)
  )
    return defaultGroup._id

  return allGroups().then((groups) => {
    for (const group of groups) {
      if (
        !isGroupNotAvailableForEventChanges(group, account) &&
        !isGroupArchived(us, group._id)
      )
        return group._id
    }
    return undefined
  })
}
